import { FC, MouseEvent } from 'react';
import { useSelector } from 'react-redux';
import {
  tailwindOverride,
  TableRow,
  Typography,
} from '@getsynapse/design-system';
import { Project } from 'types/store/projectsList';
import { formatColumnDateValue } from 'Pages/ProjectsListPage/helpers/formatters';
import { isProjectCompletionDatePast } from '../../helpers';
import { useProjectTableRow } from 'Pages/ProjectsListPage/hooks/useProjectTableRow/useProjectTableRow';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ProjectOwners from '../ProjectOwners/ProjectOwners';
import ProjectStatusTag from '../ProjectStatusTag/ProjectStatusTag';
import ProjectBusinessUnits from '../ProjectBusinessUnits/ProjectBusinessUnits';
import ProjectPrograms from '../ProjectPrograms/ProjectPrograms';
import ProjectLDTeams from '../ProjectLDTeams/ProjectLDTeams';
import TruncatedTableColumn from '../TruncatedTableColumn/TruncatedTableColumn';
import ProjectHealth from '../ProjectHealth/ProjectHealth';
import ProjectPriority from '../ProjectPriority/ProjectPriority';
import ProjectProcess from '../ProjectProcess/ProjectProcess';
import ProcessStage from '../ProjectProcess/ProcessStage';
import ProjectResourcingType from '../ProjectResourcingType/ProjectResourcingType';
import SelectProject from '../SelectProjectsCheckbox/SelectProject/SelectProject';
import QuickActions from './components/QuickActions/QuickActions';
import { LinkTableRowCell } from 'Atoms/LinkTableRowCell';
import { PATHS } from 'utils/constants';
import { StatusTableRowCell } from 'Atoms/EditableTableRowCell/StatusTableRowCell/StatusTableRowCell';
import { useUpdateProjectStatus } from 'Pages/ProjectsListPage/hooks/useUpdateProjectStatus/useUpdateProjectStatus';
import { getStatusColumn } from 'Pages/ProjectsListPage/helpers/tableColumnsValues';
import { selectProjectPermissionsById } from 'state/ProjectsList/projectsListSlice';
import { RootState } from 'state/store';
import {
  PilotPagesToTrack,
  StatusInlineEditingEntityType,
} from 'utils/customTypes';

interface ProjectsTableRowProps {
  project: Project;
  isOdd?: boolean;
  isTableEmpty?: boolean;
  isConsolidatedView?: boolean;
  onOpenCancelModal?: (projectId: string) => void;
  onOpenOnHoldModal?: (projectId: string) => void;
  onOpenCloseModal?: (projectId: string) => void;
  onRefresh?: () => void;
}

const ProjectsTableRow: FC<ProjectsTableRowProps> = ({
  project,
  isOdd,
  isTableEmpty,
  isConsolidatedView = false,
  onOpenCancelModal,
  onOpenOnHoldModal,
  onOpenCloseModal,
  onRefresh,
}) => {
  const { inlineEditingProjectStatusProjects = false } = useFlags();
  const { handleClickProjectRow, isProjectSelected } = useProjectTableRow({
    projectId: project.id,
  });
  const { handleSaveProjectStatus, getProjectStatuses } =
    useUpdateProjectStatus();

  const isCompletionDatePast = isProjectCompletionDatePast(
    project.status,
    project.targetCompletionDate
  );
  const bgColor = tailwindOverride({
    'bg-neutral-lightest-two': isOdd && !isProjectSelected,
    'bg-neutral-white': !isOdd && !isProjectSelected,
  });
  const columnStyles = tailwindOverride(
    'group-hover:border-secondary-darker group-hover:shadow-none',
    'border-b border-t border-r-0 border-transparent',
    'px-2 py-3',
    bgColor,
    {
      'bg-secondary-lightest border-secondary-darker shadow-none':
        isProjectSelected,
      'shadow-table-column': !isProjectSelected,
    }
  );
  const stickyColumnStyles = tailwindOverride(columnStyles, {
    'sticky z-1': isConsolidatedView,
  });

  const permissions = useSelector((state: RootState) =>
    selectProjectPermissionsById(state, project.id)
  );
  const hasEditPermission = permissions?.canEdit ?? false;
  const canEditStatus = hasEditPermission && !project.isArchived;
  const isOnlyStatusViewMode = !canEditStatus;

  return (
    <TableRow
      className={tailwindOverride(
        'group relative',
        'hover:shadow-lifted',
        bgColor,
        {
          'cursor-pointer': !inlineEditingProjectStatusProjects,
        }
      )}
      key={project.id}
      {...(!inlineEditingProjectStatusProjects && {
        onClick: handleClickProjectRow,
      })}
      selected={isProjectSelected}
      cells={[
        ...(isConsolidatedView
          ? [
              {
                content: <SelectProject projectId={project.id} />,
                className: tailwindOverride(
                  stickyColumnStyles,
                  'group-hover:shadow-first-table-cell-hover',
                  {
                    'w-12 left-0': !isTableEmpty && isConsolidatedView,
                  }
                ),
              },
            ]
          : []),
        {
          content: (
            <div
              className={tailwindOverride({
                'w-26': !isTableEmpty,
                'w-full': isTableEmpty,
              })}
            >
              {project.displayId}
            </div>
          ),
          className: tailwindOverride(stickyColumnStyles, {
            'w-26 left-10': !isTableEmpty && isConsolidatedView,
          }),
        },
        {
          content: inlineEditingProjectStatusProjects ? (
            <LinkTableRowCell
              value={project.name}
              to={`${PATHS.PROJECT_PAGE}/${project.id}`}
              className={tailwindOverride({
                'w-70 max-w-70': !isTableEmpty,
                'w-full': isTableEmpty,
              })}
            />
          ) : (
            <TruncatedTableColumn
              content={project.name}
              className={tailwindOverride({
                'w-70 max-w-70': !isTableEmpty,
                'w-full': isTableEmpty,
              })}
            />
          ),
          className: tailwindOverride(stickyColumnStyles, {
            'w-70 left-42': !isTableEmpty && isConsolidatedView,
          }),
        },
        {
          content: inlineEditingProjectStatusProjects ? (
            <StatusTableRowCell
              value={project.status}
              customizedValue={(status) =>
                getStatusColumn(
                  status,
                  false,
                  '',
                  inlineEditingProjectStatusProjects,
                  canEditStatus
                )
              }
              options={getProjectStatuses(project)}
              isEditable={!isOnlyStatusViewMode}
              className='w-32'
              isTaskProjectDisabled={false}
              canInlineEditTask={true}
              onSave={(newValue) => {
                handleSaveProjectStatus(
                  newValue,
                  canEditStatus,
                  project,
                  onOpenCancelModal,
                  onOpenOnHoldModal,
                  onOpenCloseModal
                );
              }}
              canEdit={hasEditPermission}
              entityType={StatusInlineEditingEntityType.PROJECT}
              userPilotEventOrigin={PilotPagesToTrack.CENTRALIZED_PROJECT}
              isArchived={project.isArchived}
            />
          ) : (
            <ProjectStatusTag
              status={project.status}
              className={tailwindOverride({
                'w-32': !isTableEmpty,
                'w-full': isTableEmpty,
              })}
            />
          ),
          className: tailwindOverride(columnStyles, {
            'w-32': !isTableEmpty,
            'p-0': inlineEditingProjectStatusProjects,
          }),
        },
        ...(isConsolidatedView
          ? [
              {
                content: (
                  <ProjectPriority
                    priority={project.priority}
                    className={tailwindOverride({
                      'w-32': !isTableEmpty,
                      'w-full': isTableEmpty,
                    })}
                  />
                ),
                className: tailwindOverride(columnStyles, {
                  'w-32': !isTableEmpty,
                }),
              },
            ]
          : []),
        {
          content: (
            <ProjectOwners
              owners={project.owners}
              className={tailwindOverride({
                'w-44': !isTableEmpty,
                'w-full': isTableEmpty,
              })}
            />
          ),
          className: tailwindOverride(columnStyles, {
            'w-44': !isTableEmpty,
          }),
        },
        ...(isConsolidatedView
          ? [
              {
                content: (
                  <ProjectHealth
                    health={project.health}
                    className={tailwindOverride({
                      'w-32': !isTableEmpty,
                      'w-full': isTableEmpty,
                    })}
                  />
                ),
                className: tailwindOverride(columnStyles, {
                  'w-32': !isTableEmpty,
                }),
              },
              {
                content: (
                  <TruncatedTableColumn
                    content={<ProjectPrograms programs={project.programs} />}
                    className={tailwindOverride({
                      'w-36': !isTableEmpty,
                      'w-full': isTableEmpty,
                    })}
                  />
                ),
                className: tailwindOverride(columnStyles, {
                  'w-36': !isTableEmpty,
                }),
              },
              {
                content: (
                  <TruncatedTableColumn
                    content={
                      <ProjectBusinessUnits teams={project.businessTeams} />
                    }
                    className={tailwindOverride({
                      'w-56': !isTableEmpty,
                      'w-full': isTableEmpty,
                    })}
                  />
                ),
                className: tailwindOverride(columnStyles, {
                  'w-56': !isTableEmpty,
                }),
              },
              {
                content: (
                  <TruncatedTableColumn
                    content={
                      <ProjectLDTeams
                        teams={project.teams}
                        privacy={project.privacy}
                      />
                    }
                    className={tailwindOverride({
                      'w-40': !isTableEmpty,
                      'w-full': isTableEmpty,
                    })}
                  />
                ),
                className: tailwindOverride(columnStyles, {
                  'w-40': !isTableEmpty,
                }),
              },
              {
                content: (
                  <TruncatedTableColumn
                    content={project.category}
                    className={tailwindOverride({
                      'w-48': !isTableEmpty,
                      'w-full': isTableEmpty,
                    })}
                  />
                ),
                className: tailwindOverride(columnStyles, {
                  'w-48': !isTableEmpty,
                }),
              },
            ]
          : []),
        {
          content: (
            <div
              className={tailwindOverride({
                'w-32': !isTableEmpty,
                'w-full': isTableEmpty,
              })}
            >
              {formatColumnDateValue(project.startDate)}
            </div>
          ),
          className: tailwindOverride(columnStyles, {
            'w-32': !isTableEmpty && isConsolidatedView,
          }),
        },
        {
          content: (
            <div
              className={tailwindOverride({
                'w-32': !isTableEmpty,
                'w-full': isTableEmpty,
              })}
            >
              <Typography
                variant='label'
                className={tailwindOverride({
                  'bg-error-lightest text-error-darker': isCompletionDatePast,
                })}
              >
                {formatColumnDateValue(project.targetCompletionDate)}
              </Typography>
            </div>
          ),
          className: tailwindOverride(columnStyles, {
            'w-32': !isTableEmpty && isConsolidatedView,
          }),
        },
        ...(isConsolidatedView
          ? [
              {
                content: (
                  <div
                    className={tailwindOverride({
                      'w-40': !isTableEmpty,
                      'w-full': isTableEmpty,
                    })}
                  >
                    {formatColumnDateValue(project.actualCompletionDate)}
                  </div>
                ),
                className: tailwindOverride(columnStyles, {
                  'w-40': !isTableEmpty,
                }),
              },
              {
                content: (
                  <div
                    className={tailwindOverride({
                      'w-32': !isTableEmpty,
                      'w-full': isTableEmpty,
                    })}
                  >
                    {formatColumnDateValue(project.updatedAt)}
                  </div>
                ),
                className: tailwindOverride(columnStyles, {
                  'w-32': !isTableEmpty,
                }),
              },
              {
                content: (
                  <ProjectResourcingType
                    resourcingType={project.resourcingType}
                    className={tailwindOverride({
                      'w-36': !isTableEmpty,
                      'w-full': isTableEmpty,
                    })}
                  />
                ),
                className: tailwindOverride(columnStyles, {
                  'w-36': !isTableEmpty,
                }),
              },
              {
                content: (
                  <TruncatedTableColumn
                    content={<ProjectProcess process={project.process} />}
                    className={tailwindOverride({
                      'w-36': !isTableEmpty,
                      'w-full': isTableEmpty,
                    })}
                  />
                ),
                className: tailwindOverride(columnStyles, {
                  'w-36': !isTableEmpty,
                }),
              },
              {
                content: (
                  <TruncatedTableColumn
                    content={<ProcessStage stage={project.stage} />}
                    className={tailwindOverride({
                      'w-36': !isTableEmpty,
                      'w-full': isTableEmpty,
                    })}
                  />
                ),
                className: tailwindOverride(columnStyles, {
                  'w-36': !isTableEmpty,
                }),
              },
              {
                content: (
                  <div
                    className={tailwindOverride(
                      'h-full text-center relative flex space-x-2',
                      {
                        'w-12': !isTableEmpty,
                        'w-full': isTableEmpty,
                      }
                    )}
                    onClick={(event: MouseEvent<HTMLDivElement>) =>
                      event.stopPropagation()
                    }
                  >
                    <QuickActions projectId={project.id} />
                  </div>
                ),
                className: tailwindOverride(stickyColumnStyles, 'w-6 right-0'),
              },
            ]
          : [
              {
                content: (
                  <div
                    className={tailwindOverride({
                      'w-32': !isTableEmpty,
                      'w-full': isTableEmpty,
                    })}
                  >
                    {formatColumnDateValue(project.updatedAt)}
                  </div>
                ),
              },
            ]),
      ]}
    />
  );
};

export default ProjectsTableRow;
